import React from "react";
import styled from "styled-components";
import COLORS from "../../../assets/Colors";
import { media } from "../theme/breakpoints";
import Badge from "../components/Badge";
import theme from "../../../assets/theme";

const RoomOccupancy = ({ rooms = [] }) => {
  const roomsInUse = rooms.filter((r) => r.status === "IN USE").length;
  const utilizationPct =
    rooms.length > 0 ? Math.round((roomsInUse / rooms.length) * 100) : 0;

  return (
    <Card $padding="0" style={{ overflow: "hidden", borderRadius: "20px" }}>
      <Header>
        <HeaderTitle>Room Occupancy</HeaderTitle>
        <HeaderSummary>
          {roomsInUse} of {rooms.length} rooms in use
        </HeaderSummary>
      </Header>

      <UtilizationSection>
        <UtilizationLabel>Today's Utilization</UtilizationLabel>
        <ProgressContainer>
          <ProgressTrack>
            <ProgressFill $pct={utilizationPct} />
          </ProgressTrack>
          <ProgressValue>{utilizationPct}%</ProgressValue>
        </ProgressContainer>
      </UtilizationSection>

      <RoomList>
        {rooms.map((room) => (
          <RoomRow key={room.roomCode} $inUse={room.status === "IN USE"}>
            <RoomInfo>
              <div>
                <RoomName>
                  {room.roomCode}
                  {room.isBackupRoom && <BackupBadge>Backup</BackupBadge>}
                </RoomName>
                <RoomMeta>
                  {room.roomCode} • {room.capacity} pax
                  {room.size && ` • ${room.size}`}
                </RoomMeta>
              </div>
            </RoomInfo>

            <StatusContainer>
              {room.status === "IN USE" ? (
                <InUseBlock>
                  <InUseLabel>IN USE</InUseLabel>
                  <InUseCount>
                    {room.sessions} session{room.sessions > 1 ? "s" : ""}
                  </InUseCount>
                </InUseBlock>
              ) : (
                <Badge color="grey" size="sm">
                  Available
                </Badge>
              )}
            </StatusContainer>
          </RoomRow>
        ))}
      </RoomList>
    </Card>
  );
};

export default RoomOccupancy;

const Card = styled.div`
  background: ${COLORS.PRIMARY_WHITE};
  border: 1px solid ${COLORS.BORDER_GREY};
  border-radius: 10px;
  padding: ${({ $padding }) =>
    $padding !== undefined ? `${$padding}px` : "22px"};
`;

const Header = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid ${COLORS.BORDER_GREY};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(to right, #f8fafc, #f1f5f9);
`;

const HeaderTitle = styled.h3`
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 17px;
  font-weight: 800;
  color: ${COLORS.COLOR_DARK};
  margin: 0;
`;

const HeaderSummary = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13.5px;
  color: #64748b;
`;

const UtilizationSection = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid ${COLORS.BORDER_GREY};
  background: #f8fafc;
`;

const UtilizationLabel = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
  margin-bottom: 10px;
`;

const ProgressContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ProgressTrack = styled.div`
  flex: 1;
  height: 8px;
  background: #e2e8f0;
  border-radius: 9999px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: linear-gradient(
    90deg,
    ${({ $pct }) =>
        $pct >= 80 ? "#ef4444" : $pct >= 60 ? "#f59e0b" : "#00838c"}
      0%,
    ${({ $pct }) =>
        $pct >= 80 ? "#dc2626" : $pct >= 60 ? "#d97706" : "#00838c"}
      100%
  );
  border-radius: 9999px;
  transition: width 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
`;

const ProgressValue = styled.div`
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 18px;
  font-weight: 800;
  color: ${COLORS.COLOR_DARK};
  min-width: 52px;
  text-align: right;
`;

const RoomList = styled.div``;

const RoomRow = styled.div`
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${COLORS.BORDER_GREY};
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }

  ${media.tablet} {
    padding: 18px 28px;
  }
`;

const RoomInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
  min-width: 0;
`;

const RoomName = styled.div`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 15px;
  font-weight: 700;
  color: ${COLORS.COLOR_DARK};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BackupBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  background: #fef3c7;
  color: #c98d18;
  padding: 1px 6px;
  border-radius: 9999px;
`;

const RoomMeta = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12.5px;
  color: #64748b;
  margin-top: 2px;
`;

const StatusContainer = styled.div`
  flex-shrink: 0;
`;

const InUseBlock = styled.div`
  text-align: right;
`;

const InUseLabel = styled.div`
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 11px;
  font-weight: 800;
  color: ${COLORS.BTN_GREEN};
  letter-spacing: 0.5px;
`;

const InUseCount = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
`;
